import React from 'react';

import { RainmakerLogo } from '@/components/rainmaker';
import { cn } from '@/lib/utils';
import { DomainEventSchema } from '@/services/api/matterChangesService';

import { CollapsibleList } from './CollapsibleList';
import { extractChangedValues } from './diffUtil';
import { formatFieldName, formatStatus } from './utils';

export interface ActivityDiffListProps {
  diff: Record<string, any> | null | undefined;
  formatValue?: (field: string, value: any) => string;
  showLabel?: string;
  hideLabel?: string;
  defaultExpanded?: boolean;
  className?: string;
}

/**
 * Helper to extract user information from event
 */
export const getUserFromEvent = (event: DomainEventSchema) => {
  if (event.user) {
    const fullName = [event.user.first_name, event.user.last_name]
      .filter(Boolean)
      .join(' ');
    return {
      name: fullName || event.user.username || event.user.email,
      avatarUrl: undefined,
    };
  }

  if (event.agent) {
    // Special icon for Rainmaker agent
    // Note: 'rainmaker' may not be in generated AgentType yet, but it exists in the backend
    const avatarIcon =
      (event.agent as string) === 'rainmaker' ? (
        <RainmakerLogo size={16} className="text-violet-500" />
      ) : (
        <div className="text-xs">🤖</div>
      );

    return {
      name: formatAgentName(event.agent),
      avatarIcon,
    };
  }

  return undefined;
};

/**
 * Format agent type into readable name
 */
export const formatAgentName = (agent: string): string => {
  const agentNames: Record<string, string> = {
    ingest_transcript_task: 'Transcript Processor',
    provider_finder: 'Provider Finder',
    artifact_digest: 'Document Analyzer',
    treatment_researcher: 'Treatment Researcher',
    treatment_request_automation: 'Request Automation',
    bland_caller: 'Voice AI',
    slack_task_bot: 'Task Bot',
    blob_sorter: 'Document Sorter',
    front_app_integration: 'Front Integration',
    task_generator: 'Task Generator',
    coverage_extractor: 'Coverage Extractor',
    airtable_sync: 'Airtable Sync',
    loa_detector: 'LOA Detector',
    rainmaker: 'Rainmaker',
    system: 'System',
    backfill_script: 'Backfill Script',
    domain_event_update_orchestrator: 'Domain Event Orchestrator',
  };

  return agentNames[agent] || agent.replace(/_/g, ' ');
};

const defaultFormatValue = (field: string, value: any): string => {
  if (value === null || value === undefined) return '';

  // Format status fields
  if (field === 'status' || field.endsWith('_status')) {
    return formatStatus(String(value));
  }

  // Format dates
  if (
    field.includes('date') ||
    field.includes('_at') ||
    field === 'created' ||
    field === 'updated'
  ) {
    try {
      const date = new Date(value);
      if (!Number.isNaN(date.getTime())) {
        return date.toLocaleString();
      }
    } catch {
      // If parsing fails, fall through to default
    }
  }

  // Format booleans
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  return String(value);
};

const getDiffChanges = (diff: Record<string, unknown>) => {
  try {
    const changes = extractChangedValues(diff);

    if (changes.length === 0) {
      return diff;
    }

    return changes.reduce(
      (acc, change) => {
        acc[change.key] = change.newValue;
        return acc;
      },
      {} as Record<string, unknown>,
    );
  } catch {
    return diff;
  }
};

export const ActivityDiffList: React.FC<ActivityDiffListProps> = ({
  diff,
  formatValue = defaultFormatValue,
  showLabel,
  hideLabel,
  defaultExpanded = false,
  className,
}) => {
  if (!diff || Object.keys(diff).length === 0) {
    return null;
  }

  const changes = Object.entries(getDiffChanges(diff)).map(([field, value]) => (
    <div key={field} className="flex gap-2">
      <span className="font-medium text-gray-700">
        {formatFieldName(field)}:
      </span>
      <span className="text-gray-900">{formatValue(field, value)}</span>
    </div>
  ));

  const changeCount = changes.length;

  return (
    <CollapsibleList
      items={changes}
      showLabel={
        showLabel || `Show ${changeCount} change${changeCount > 1 ? 's' : ''}`
      }
      hideLabel={hideLabel || 'Hide changes'}
      defaultExpanded={defaultExpanded}
      className={cn(className)}
    />
  );
};
